import type { PinCreateRequest, PinView } from "@screener/protocol";
import { useCallback, useEffect, useState } from "react";
import type { PinMemory } from "./pin-match";
import { createPin, deletePin, fetchPins } from "./pins-api";

/** Состояние списка закреплений §6.7 — тот же снаряд, что у заметок. */
export type PinsState =
  | { status: "loading" }
  | { status: "ready"; pins: PinView[] }
  | { status: "unavailable" };

interface UsePinsOptions {
  onSessionInvalid: () => void;
}

interface UsePinsResult {
  state: PinsState;
  /**
   * Память вкладки о плотности, с которой поставлено каждое закрепление
   * (design.md решение 1a) — используется `matchPinsToRows` для подсветки
   * «якорь сдвинулся», когда точного совпадения уровня больше нет.
   */
  memory: PinMemory;
  /**
   * Закрепить уровень строки `densityId` или снять уже стоящее закрепление
   * тем же действием.
   */
  toggle: (
    existing: PinView | undefined,
    level: PinCreateRequest,
    densityId: string,
  ) => Promise<"ok" | "invalid" | "not-found" | "unavailable">;
  /**
   * Забыть память о плотностях, которых больше нет в потоке §7.6 (design.md
   * решение 1a: память живёт, пока жива плотность, с которой закрепление
   * поставлено). Вызывающая сторона передаёт полный состав живых `density_id`.
   */
  forgetDeadDensities: (liveDensityIds: ReadonlySet<string>) => void;
}

/**
 * Состояние перед перечитыванием списка — тот же приём, что у заметок
 * (`beforeNotesReload`): уже прочитанный список остаётся видимым во время
 * `GET`, вызванного собственным действием (`use-notes.ts`).
 */
export function beforePinsReload(previous: PinsState): PinsState {
  return previous.status === "ready" ? previous : { status: "loading" };
}

/**
 * Закрепления §6.7 (1.18) общим списком пользователя: загрузка при входе,
 * перезагрузка после собственного действия, без опроса (design.md решение 4).
 */
export function usePins({ onSessionInvalid }: UsePinsOptions): UsePinsResult {
  const [state, setState] = useState<PinsState>({ status: "loading" });
  const [memory, setMemory] = useState<PinMemory>(new Map());

  const load = useCallback(() => {
    setState(beforePinsReload);
    fetchPins().then((result) => {
      if (result.status === "unauthorized") {
        onSessionInvalid();
        return;
      }
      setState(
        result.status === "ok" ? { status: "ready", pins: result.pins } : { status: "unavailable" },
      );
    });
  }, [onSessionInvalid]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = useCallback(
    async (
      existing: PinView | undefined,
      level: PinCreateRequest,
      densityId: string,
    ): Promise<"ok" | "invalid" | "not-found" | "unavailable"> => {
      if (existing !== undefined) {
        const result = await deletePin(existing.id);
        if (result.status === "unauthorized") {
          onSessionInvalid();
          return "unavailable";
        }
        if (result.status === "ok") {
          setMemory((previous) => {
            const next = new Map(previous);
            next.delete(existing.id);
            return next;
          });
          load();
        }
        return result.status;
      }

      const result = await createPin(level);
      if (result.status === "unauthorized") {
        onSessionInvalid();
        return "unavailable";
      }
      if (result.status === "ok") {
        setMemory((previous) => new Map(previous).set(result.pin.id, densityId));
        load();
      }
      return result.status;
    },
    [load, onSessionInvalid],
  );

  const forgetDeadDensities = useCallback((liveDensityIds: ReadonlySet<string>) => {
    setMemory((previous) => {
      let changed = false;
      const next = new Map(previous);
      for (const [pinId, densityId] of previous) {
        if (!liveDensityIds.has(densityId)) {
          next.delete(pinId);
          changed = true;
        }
      }
      return changed ? next : previous;
    });
  }, []);

  return { state, memory, toggle, forgetDeadDensities };
}
